import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Button,
    Collapse,
    Empty,
    Form,
    Input,
    Modal,
    Select,
    Space,
    Spin,
    Tabs,
    message,
} from 'antd';
import { useApi, type InvoiceDocumentTemplatePayload } from '../contexts/ApiContext';
import {
    buildDocumentVariableGroups,
    INVOICE_DOCUMENT_VARIABLE_GROUP_SPECS,
} from '../domain/documentVariables';
import RichTextEditor from './RichTextEditor';
import VisualHtmlDocumentBuilder from './VisualHtmlDocumentBuilder';

interface InvoiceDocumentEditorProps {
    open: boolean;
    onClose: () => void;
}

const PREVIEW_STYLE: CSSProperties = {
    width: '100%',
    height: '70vh',
    border: '1px solid #d9d9d9',
};
const LOADING_STYLE: CSSProperties = { textAlign: 'center', padding: 48 };
const EMPTY_PREVIEW_STYLE: CSSProperties = { padding: 24 };

const InvoiceDocumentEditor = ({ open, onClose }: InvoiceDocumentEditorProps) => {
    const { t } = useTranslation();
    const { invoiceDocument } = useApi();
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [template, setTemplate] = useState<InvoiceDocumentTemplatePayload | null>(null);
    const [templatesList, setTemplatesList] = useState<InvoiceDocumentTemplatePayload[]>([]);
    const [name, setName] = useState('');
    const [htmlContent, setHtmlContent] = useState('');
    const [cssContent, setCssContent] = useState('');
    const [previewNumber, setPreviewNumber] = useState('');
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState('richText');
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const original = useRef({ name: '', htmlContent: '', cssContent: '' });
    const dirty = useRef({ name: false, htmlContent: false, cssContent: false });
    const loadSeq = useRef(0);
    const previewSeq = useRef(0);

    const variableGroups = useMemo(
        () =>
            buildDocumentVariableGroups(INVOICE_DOCUMENT_VARIABLE_GROUP_SPECS, t, labelKey =>
                t(`configuration.invoiceDocument.variables.${labelKey}`),
            ),
        [t],
    );

    const templateOptions = useMemo(
        () =>
            templatesList.map(t => ({
                value: t.id,
                label: t.name || `Template #${t.id}`,
            })),
        [templatesList],
    );

    const applyTemplate = useCallback((next: InvoiceDocumentTemplatePayload) => {
        const values = {
            name: next.name ?? '',
            htmlContent: next.htmlContent ?? '',
            cssContent: next.cssContent ?? '',
        };
        setTemplate(next);
        setName(values.name);
        setHtmlContent(values.htmlContent);
        setCssContent(values.cssContent);
        original.current = values;
        dirty.current = { name: false, htmlContent: false, cssContent: false };
    }, []);

    const loadTemplatesList = useCallback(async () => {
        const response = await invoiceDocument.listTemplates();
        if (response.success && response.data) {
            setTemplatesList(response.data.templates);
        }
    }, [invoiceDocument]);

    const loadTemplate = useCallback(
        async (templateId?: number) => {
            const seq = ++loadSeq.current;
            setLoading(true);
            try {
                const response = templateId
                    ? await invoiceDocument.getTemplate(templateId)
                    : await invoiceDocument.getActiveTemplate();
                // Discard stale responses if a newer loadTemplate call was made
                if (seq !== loadSeq.current) return;
                if (response.success && response.data) {
                    applyTemplate(response.data.template);
                } else {
                    setTemplate(null);
                    message.error(
                        response.error ?? t('configuration.invoiceDocument.errors.loadFailed'),
                    );
                }
            } finally {
                if (seq === loadSeq.current) setLoading(false);
            }
        },
        [applyTemplate, invoiceDocument, t],
    );

    useEffect(() => {
        if (!open) return;
        loadTemplatesList();
        void loadTemplate();
    }, [loadTemplatesList, loadTemplate, open]);

    /**
     * Closing the preview both hides the modal and drops what it was showing: the
     * document lives in a blob URL behind an iframe, so keeping it would leave a PDF
     * frame alive under a hidden modal and show the previous invoice for a moment the
     * next time the modal opens. Bumping the sequence is what makes the close stick —
     * a preview request that is still in flight resolves into a discarded sequence
     * instead of re-opening the modal the user has just dismissed.
     */
    const closePreview = useCallback(() => {
        previewSeq.current++;
        setPreviewModalOpen(false);
        setPreviewError(null);
        // The cleanup below revokes the blob URL as soon as this clears it.
        setPreviewUrl(null);
    }, []);

    useEffect(() => {
        if (open) return;
        // Invalidate any in-flight loadTemplate calls from a previous session
        loadSeq.current = 0;
        setTemplatesList([]);
        // The preview belongs to the editor session; it must not outlive it.
        closePreview();
    }, [closePreview, open]);

    useEffect(
        () => () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        },
        [previewUrl],
    );

    const draftPayload = () => ({
        name: dirty.current.name ? name : original.current.name,
        htmlContent: dirty.current.htmlContent ? htmlContent : original.current.htmlContent,
        cssContent: dirty.current.cssContent ? cssContent : original.current.cssContent,
    });

    const saveDraft = async () => {
        if (!template) return;
        setBusy(true);
        try {
            const response = await invoiceDocument.saveDraft(template.id, draftPayload());
            if (response.success && response.data) {
                applyTemplate(response.data.template);
                message.success(t('configuration.invoiceDocument.messages.draftSaved'));
            } else {
                message.error(
                    response.error ?? t('configuration.invoiceDocument.errors.saveFailed'),
                );
            }
        } finally {
            setBusy(false);
        }
    };

    const handleSaveAsNew = async () => {
        if (!template) return;
        const newName = window.prompt(
            t('configuration.invoiceDocument.saveAsNewName') || 'Enter new template name:',
        );
        if (!newName?.trim()) return;

        setBusy(true);
        try {
            const response = await invoiceDocument.duplicate(template.id, {
                ...draftPayload(),
                name: newName,
            });
            if (response.success && response.data) {
                applyTemplate(response.data.template);
                message.success(t('configuration.invoiceDocument.messages.draftSaved'));
                loadTemplatesList();
            } else {
                message.error(
                    response.error ?? t('configuration.invoiceDocument.errors.saveFailed'),
                );
            }
        } finally {
            setBusy(false);
        }
    };

    const publish = async () => {
        if (!template) return;
        setBusy(true);
        try {
            const saved = await invoiceDocument.saveDraft(template.id, draftPayload());
            if (!saved.success) {
                message.error(saved.error ?? t('configuration.invoiceDocument.errors.saveFailed'));
                return;
            }
            const response = await invoiceDocument.publish(template.id);
            if (response.success && response.data) {
                applyTemplate(response.data.template);
                message.success(t('configuration.invoiceDocument.messages.published'));
            } else {
                message.error(
                    response.error ?? t('configuration.invoiceDocument.errors.publishFailed'),
                );
            }
        } finally {
            setBusy(false);
        }
    };

    const restoreDefault = () => {
        if (!template) return;
        Modal.confirm({
            title: t('configuration.invoiceDocument.confirmRestoreTitle'),
            content: t('configuration.invoiceDocument.confirmRestoreContent'),
            okText: t('common.confirm'),
            cancelText: t('common.cancel'),
            onOk: async () => {
                setBusy(true);
                try {
                    const factory = await invoiceDocument.getFactoryDefault();
                    if (!factory.success || !factory.data) {
                        message.error(
                            factory.error ??
                                t('configuration.invoiceDocument.errors.factoryDefaultFailed'),
                        );
                        return;
                    }
                    const response = await invoiceDocument.restoreDefault(template.id, {
                        defaultHtmlContent: factory.data.htmlContent,
                        defaultCssContent: factory.data.cssContent,
                    });
                    if (response.success && response.data) {
                        applyTemplate(response.data.template);
                        message.success(t('configuration.invoiceDocument.messages.restored'));
                    } else {
                        message.error(
                            response.error ??
                                t('configuration.invoiceDocument.errors.restoreFailed'),
                        );
                    }
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    const preview = async () => {
        const invoiceNumber = previewNumber.trim();
        if (!invoiceNumber) {
            message.warning(t('configuration.invoiceDocument.errors.invoiceNumberRequired'));
            return;
        }
        const seq = ++previewSeq.current;
        setBusy(true);
        setPreviewError(null);
        try {
            const response = await invoiceDocument.previewByInvoiceNumber(
                invoiceNumber,
                template?.id,
            );
            // Discard a response the user has closed away from, or that a newer
            // preview has superseded — neither may re-open the modal.
            if (seq !== previewSeq.current) return;
            if (response.success && response.data) {
                if (previewUrl) URL.revokeObjectURL(previewUrl);
                setPreviewUrl(URL.createObjectURL(response.data));
            } else {
                setPreviewError(
                    response.error ?? t('configuration.invoiceDocument.errors.previewFailed'),
                );
            }
            setPreviewModalOpen(true);
        } finally {
            setBusy(false);
        }
    };

    const previewBody = useMemo(() => {
        if (previewError) {
            return <Alert type="error" message={previewError} />;
        }
        if (previewUrl) {
            return (
                <iframe title="invoice-document-preview" src={previewUrl} style={PREVIEW_STYLE} />
            );
        }
        return (
            <Empty
                description={t('configuration.invoiceDocument.previewEmpty')}
                style={EMPTY_PREVIEW_STYLE}
            />
        );
    }, [previewError, previewUrl, t]);

    return (
        <>
            <Modal
                open={open}
                onCancel={onClose}
                width="calc(92vw - 180px)"
                zIndex={1300}
                style={{ top: 24 }}
                styles={{ wrapper: { paddingLeft: '180px' } }}
                title={
                    <Space>
                        {t('configuration.invoiceDocument.editorTitle')}
                        <Select
                            value={template?.id}
                            style={{ width: 200, marginLeft: 16 }}
                            placeholder={t('configuration.invoiceDocument.selectTemplate')}
                            options={templateOptions}
                            onChange={val => void loadTemplate(val)}
                            disabled={loading || busy}
                        />
                    </Space>
                }
                footer={[
                    <Button key="close" onClick={onClose}>
                        {t('common.close')}
                    </Button>,
                    <Button key="restore" onClick={restoreDefault} disabled={!template || busy}>
                        {t('configuration.invoiceDocument.restoreDefault')}
                    </Button>,
                    <Button
                        key="draft"
                        onClick={saveDraft}
                        disabled={!template || busy}
                        loading={busy}
                    >
                        {t('configuration.invoiceDocument.saveDraft')}
                    </Button>,
                    <Button
                        key="saveAsNew"
                        onClick={handleSaveAsNew}
                        disabled={!template || busy}
                        loading={busy}
                    >
                        {t('configuration.invoiceDocument.saveAsNew') || 'Save As New'}
                    </Button>,
                    <Button
                        key="publish"
                        type="primary"
                        onClick={publish}
                        disabled={!template || busy}
                        loading={busy}
                    >
                        {t('configuration.invoiceDocument.publish')}
                    </Button>,
                ]}
            >
                {loading ? (
                    <div style={LOADING_STYLE}>
                        <Spin />
                    </div>
                ) : null}
                {!loading && !template ? (
                    <Empty description={t('configuration.invoiceDocument.noTemplate')} />
                ) : null}
                {!loading && template ? (
                    <Form layout="vertical">
                        <Form.Item label={t('configuration.invoiceDocument.templateName')}>
                            <Input
                                value={name}
                                maxLength={100}
                                onChange={event => {
                                    setName(event.target.value);
                                    dirty.current.name = true;
                                }}
                            />
                        </Form.Item>
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            tabBarExtraContent={{
                                right: (
                                    <Space.Compact>
                                        <Input
                                            size="small"
                                            value={previewNumber}
                                            onChange={event => setPreviewNumber(event.target.value)}
                                            placeholder={t(
                                                'configuration.invoiceDocument.previewInvoiceNumber',
                                            )}
                                            style={{ width: 220 }}
                                            disabled={busy}
                                        />
                                        <Button
                                            size="small"
                                            type="primary"
                                            onClick={preview}
                                            loading={busy}
                                            disabled={!template}
                                        >
                                            {t('configuration.invoiceDocument.previewButton')}
                                        </Button>
                                    </Space.Compact>
                                ),
                            }}
                            items={[
                                {
                                    key: 'richText',
                                    label: t('configuration.invoiceDocument.tabs.richText'),
                                    children: (
                                        <>
                                            <Form.Item
                                                label={t(
                                                    'configuration.invoiceDocument.contentLabel',
                                                )}
                                                help={t(
                                                    'configuration.invoiceDocument.contentHelp',
                                                )}
                                            >
                                                <RichTextEditor
                                                    value={htmlContent}
                                                    onChange={next => {
                                                        setHtmlContent(next);
                                                        if (next !== original.current.htmlContent)
                                                            dirty.current.htmlContent = true;
                                                    }}
                                                    variableGroups={variableGroups}
                                                    disabled={busy}
                                                />
                                            </Form.Item>
                                            <Collapse
                                                size="small"
                                                items={[
                                                    {
                                                        key: 'advanced',
                                                        label: t(
                                                            'configuration.invoiceDocument.advancedTitle',
                                                        ),
                                                        children: (
                                                            <>
                                                                <Alert
                                                                    type="warning"
                                                                    showIcon
                                                                    message={t(
                                                                        'configuration.invoiceDocument.advancedHint',
                                                                    )}
                                                                    style={{ marginBottom: 8 }}
                                                                />
                                                                <Form.Item
                                                                    label={t(
                                                                        'configuration.invoiceDocument.htmlContent',
                                                                    )}
                                                                >
                                                                    <Input.TextArea
                                                                        value={htmlContent}
                                                                        onChange={event => {
                                                                            setHtmlContent(
                                                                                event.target.value,
                                                                            );
                                                                            dirty.current.htmlContent = true;
                                                                        }}
                                                                        autoSize={{
                                                                            minRows: 8,
                                                                            maxRows: 18,
                                                                        }}
                                                                        spellCheck={false}
                                                                        style={{
                                                                            fontFamily: 'monospace',
                                                                            fontSize: 12,
                                                                        }}
                                                                    />
                                                                </Form.Item>
                                                                <Form.Item
                                                                    label={t(
                                                                        'configuration.invoiceDocument.cssContent',
                                                                    )}
                                                                >
                                                                    <Input.TextArea
                                                                        value={cssContent}
                                                                        onChange={event => {
                                                                            setCssContent(
                                                                                event.target.value,
                                                                            );
                                                                            dirty.current.cssContent = true;
                                                                        }}
                                                                        autoSize={{
                                                                            minRows: 6,
                                                                            maxRows: 14,
                                                                        }}
                                                                        spellCheck={false}
                                                                        style={{
                                                                            fontFamily: 'monospace',
                                                                            fontSize: 12,
                                                                        }}
                                                                    />
                                                                </Form.Item>
                                                            </>
                                                        ),
                                                    },
                                                ]}
                                            />
                                        </>
                                    ),
                                },
                                {
                                    key: 'visualBuilder',
                                    label: t('configuration.invoiceDocument.tabs.visualBuilder'),
                                    children: (
                                        <VisualHtmlDocumentBuilder
                                            value={htmlContent}
                                            cssContent={cssContent}
                                            onChange={next => {
                                                setHtmlContent(next);
                                                dirty.current.htmlContent = true;
                                            }}
                                            variableGroups={variableGroups}
                                            disabled={busy}
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Form>
                ) : null}
            </Modal>
            {/*
              * The preview opens and closes without the zoom/fade motion, and destroys
              * itself when hidden. antd only hides a modal once rc-motion reports the
              * leave animation finished and configures no deadline for that report, so a
              * missed end event leaves the modal on screen for good — masking the editor,
              * with a close button that has nothing left to close. Without the motion the
              * close is a plain render, and destroying takes the PDF frame with it
              * instead of freezing it in the closed modal (rc-dialog stops updating a
              * hidden modal's content, so clearing the URL alone would not remove it).
              */}
            <Modal
                open={previewModalOpen}
                onCancel={closePreview}
                width="80vw"
                zIndex={1400}
                style={{ top: 24 }}
                title={t('configuration.invoiceDocument.previewButton')}
                footer={null}
                transitionName=""
                maskTransitionName=""
                destroyOnHidden
            >
                {previewBody}
            </Modal>
        </>
    );
};

export default InvoiceDocumentEditor;
